// src/app/analytics/ga.service.ts
import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

@Injectable({ providedIn: 'root' })
export class GaService {
  private loaded = false;
  private id: string | null = null;

  constructor(@Inject(PLATFORM_ID) private platformId: object) {}

  init(router: Router): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const id = (environment.gaMeasurementId || '').trim();
    if (!id || id.toLowerCase() === 'none') return; // lcl/off

    if (!this.loaded) {
      this.loadGtag(id);
      this.loaded = true;
      this.id = id;
    }

    // SPA page_view on route changes
    router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        const path = e.urlAfterRedirects || e.url;
        window.gtag('event', 'page_view', {
          page_location: location.href,
          page_path: path,
          page_title: document.title,
        });
      });
  }

  event(name: string, params: Record<string, any> = {}): void {
    if (!this.loaded) return;
    window.gtag('event', name, params);
  }

  setUserId(userId: string): void {
    if (!this.loaded || !this.id) return;
    window.gtag('config', this.id, { user_id: userId });
  }

  set(params: Record<string, any>): void {
    if (!this.loaded) return;
    window.gtag('set', params);
  }

  private loadGtag(id: string): void {
    // Inject gtag.js
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
      id
    )}`;
    document.head.appendChild(s);

    // Stub and init
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () {
      window.dataLayer.push(arguments as any);
    } as any;

    window.gtag('js', new Date());

    // Basic consent defaults (adjust to your policy)
    window.gtag('consent', 'default', {
      ad_storage: 'denied',
      analytics_storage: 'granted',
      functionality_storage: 'granted',
      security_storage: 'granted',
    });

    // Do not auto-send page_view in SPAs
    window.gtag('config', id, {
      send_page_view: false,
      transport_type: 'beacon',
      debug_mode: !environment.production,
    });
  }
}
